export const REASON_OFFLINE = "offline";
export const REASON_OUT_OF_AREA = "out.of.area";

const EARTH_RADIUS_KM = 6371;
const FALLBACK_NAME = "This supplier";

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export const haversineKm = (from, to) => {
  if (!from || !to) return null;

  const { latitude: lat1, longitude: lng1 } = from;
  const { latitude: lat2, longitude: lng2 } = to;

  if (![lat1, lng1, lat2, lng2].every((value) => Number.isFinite(value))) {
    return null;
  }

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const formatKm = (km) => {
  if (!Number.isFinite(km)) return "";

  const text = km < 10 ? km.toFixed(1) : String(Math.round(km));

  return `${text.endsWith(".0") ? text.slice(0, -2) : text} km`;
};

export const isOnlineStatus = (status) => {
  if (status === null || status === undefined || status === "") return true;

  return String(status).toLowerCase() !== "offline";
};

export const checkDeliverability = (supplier) => {
  if (!supplier) return { deliverable: true, reason: null };

  if (supplier.online === false) {
    return { deliverable: false, reason: REASON_OFFLINE };
  }

  const { deliveryRadiusKm: radiusKm, distanceKm } = supplier;

  if (!Number.isFinite(radiusKm) || radiusKm <= 0) {
    return { deliverable: true, reason: null };
  }

  if (!Number.isFinite(distanceKm)) return { deliverable: true, reason: null };

  if (distanceKm <= radiusKm) return { deliverable: true, reason: null };

  return { deliverable: false, reason: REASON_OUT_OF_AREA, radiusKm, distanceKm };
};

export const deliveryNotice = ({ reason, name, radiusKm, distanceKm } = {}) => {
  const who = name || FALLBACK_NAME;

  if (reason === REASON_OFFLINE) {
    return {
      reason,
      badge: "Offline",
      short: "This supplier is currently offline",
      title: "Supplier is offline",
      message: `${who} is not accepting orders right now. Please try again shortly, or order this from another supplier.`,
    };
  }

  if (reason === REASON_OUT_OF_AREA) {
    return {
      reason,
      badge: "Delivery unavailable",
      short: "This supplier does not deliver to your area",
      title: "Outside the delivery area",
      message: `${who} delivers within ${formatKm(radiusKm)} and your address is ${formatKm(
        distanceKm
      )} away. Choose a closer address, or order this from another supplier.`,
    };
  }

  return null;
};

export const supplierNotice = (supplier) => {
  const { deliverable, reason, radiusKm, distanceKm } = checkDeliverability(supplier);

  if (deliverable) return null;

  return deliveryNotice({
    reason,
    name: supplier?.name,
    radiusKm,
    distanceKm,
  });
};

export const deliveryUnavailableSummary = (notices) => {
  const blocked = (notices || []).filter(Boolean);
  if (!blocked.length) return null;

  const count = blocked.length;
  const subject = count === 1 ? "1 supplier" : `${count} suppliers`;

  const offline = blocked.filter((notice) => notice.reason === REASON_OFFLINE).length;
  const outOfArea = blocked.filter(
    (notice) => notice.reason === REASON_OUT_OF_AREA
  ).length;

  if (offline === count) {
    return `${subject} ${count === 1 ? "is" : "are"} offline and cannot take orders right now.`;
  }

  if (outOfArea === count) {
    return `${subject} ${count === 1 ? "does" : "do"} not deliver to your area.`;
  }

  return `${subject} cannot deliver to you right now — ${outOfArea} ${
    outOfArea === 1 ? "is" : "are"
  } outside their delivery area and ${offline} ${
    offline === 1 ? "is" : "are"
  } offline.`;
};
